/**
 * The `worst()` helper from the squarified-treemap algorithm, isolated so it can
 * be property-tested on its own (plan §3.3 · §4.5).
 *
 * Given the SCALED areas already committed to a row and the row's length along
 * the layout axis, returns the worst (largest) aspect ratio any tile in that row
 * would have. Squarify's greedy step keeps adding tiles while this number does
 * not get worse.
 *
 * Always >= 1 by construction: each tile contributes `max(r, 1/r)`, so an
 * infinitely wide tile and an infinitely tall one are equally bad. A degenerate
 * row returns +Infinity rather than NaN, so the caller's `worst <= best`
 * comparison stays well-defined instead of silently evaluating false.
 */
export function worstAspectRatio(scaledAreas: readonly number[], rowLength: number): number {
  if (scaledAreas.length === 0 || !(rowLength > 0)) return Number.POSITIVE_INFINITY;
  return scaledAreas.reduce((worst, area) => {
    const crossLength = area / rowLength;
    if (!(crossLength > 0)) return Number.POSITIVE_INFINITY;
    return Math.max(worst, rowLength / crossLength, crossLength / rowLength);
  }, 0);
}
